import { WebsiteLoader } from '@modules/loaders/interfaces/website-loader.interface';
import { parseLinearGradient, parseRadialGradient } from '@shared/helpers/gradient-helpers';

const DEFAULT_LOADER_TYPE = 'spinner';
const DEFAULT_LOADER_COLOR = '#000';

// AT-9115: nazwa szablonu to element ścieżki pliku, więc dopuszczamy wyłącznie slug
// (małe litery, cyfry, myślnik) — bez `/`, `..`, `?` ani `%`.
const LOADER_TYPE_PATTERN = /^[a-z0-9-]+$/;

// Kolor trafia do atrybutów SVG renderowanych po stronie serwera (AT-9146), więc to
// wyłącznie znaki spotykane w kolorach CSS i gradientach: hex, rgb(a), hsl, nazwy.
const LOADER_COLOR_PATTERN = /^[#A-Za-z0-9(),.%\s-]{1,200}$/;

/**
 * Zwraca typ loadera bezpieczny do wstawienia w ścieżkę szablonu; nieznany/niepoprawny
 * typ (dane najemcy) zastępujemy domyślnym.
 */
export const sanitizeLoaderType = (type: unknown): string => {
  return typeof type === 'string' && LOADER_TYPE_PATTERN.test(type) ? type : DEFAULT_LOADER_TYPE;
};

export const sanitizeLoaderColor = (color: unknown): string => {
  return typeof color === 'string' && LOADER_COLOR_PATTERN.test(color.trim()) ? color.trim() : DEFAULT_LOADER_COLOR;
};

export const getLoaderHtml = async (rawLoader: WebsiteLoader, getFn: (name: string) => Promise<string>) => {
  const loader = {
    ...rawLoader,
    type: sanitizeLoaderType(rawLoader?.type),
    color: sanitizeLoaderColor(rawLoader?.color),
  } as WebsiteLoader;
  const loaderName = '/assets/loader-templates/' + loader.type + '-loader.svg';
  const html = await getFn(loaderName);
  let color = loader.color;
  let linearGradientPosition = {
    x1: '0%',
    y1: '0%',
    x2: '100%',
    y2: '100%',
  };
  let radialGradientPosition = {
    x: '50%',
    y: '50%',
  };
  let gradientColorValues = [];
  if (loader.color.startsWith('linear-gradient')) {
    const loaderSizes = {
      'spinner': {w: 50, h: 50},
      'infinity': {w: 100, h: 50},
    };

    const loaderSize = loaderSizes[loader.type] || {w: 100, h: 100};

    const linear = parseLinearGradient(loader.color, loaderSize);
    if (linear !== null) {
      gradientColorValues = linear.gradientColorValues;
      linearGradientPosition = linear.linearGradientPosition;
    }
    color = 'url(#linear)';
  }
  if (loader.color.startsWith('radial-gradient')) {
    const radial = parseRadialGradient(loader.color);
    if (radial !== null) {
      gradientColorValues = radial.gradientColorValues;
      radialGradientPosition = radial.radialGradientPosition;
    }
    color = 'url(#radial)';
  }


  const gradients = gradientColorValues.map(stop =>
    `<stop offset="${sanitizeLoaderColor(String(stop.position))}" stop-color="${sanitizeLoaderColor(stop.color)}"/>`,
  ).join('\n');
  return html
    .replaceAll('<!--gradientColorValues-->', gradients)
    .replaceAll('{{color}}', color)
    .replaceAll('{{linearGradientPositionX1}}', linearGradientPosition.x1)
    .replaceAll('{{linearGradientPositionX2}}', linearGradientPosition.x2)
    .replaceAll('{{linearGradientPositionY1}}', linearGradientPosition.y1)
    .replaceAll('{{linearGradientPositionY2}}', linearGradientPosition.y2)
    .replaceAll('{{radialGradientPositionX}}', radialGradientPosition.x)
    .replaceAll('{{radialGradientPositionY}}', radialGradientPosition.y);
};
export const getLoaderHtmlBrowser = async (loader: WebsiteLoader) => {
  const getFn = async (name: string): Promise<string> => {
    return new Promise((resolve) => {
      fetch(name).then(response => {
        resolve(response.text());
      });
    });
  };
  return getLoaderHtml(loader, getFn);
};
